/**
 * Thư ký tòa án — Smart Legal Tech
 * Layout & API giống n2nai_erpcoach ViewPage + services/api.js (transcript, summary, quiz, flashcards, chat, history).
 */
import { useState, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import CourtSecretaryEvidencePanel from './CourtSecretaryEvidencePanel'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  alpha,
  useTheme,
  IconButton,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material'
import { SmartToy, Send, MenuBook, Quiz as QuizIcon, Style as FlashcardIcon, PlayArrow } from '@mui/icons-material'
import { useYoutubeIframePlayer } from '../../hooks/useYoutubeIframePlayer'
import { PageDataState } from '../../components/PageDataState'
import {
  useGetErpcoachTranscriptQuery,
  useGetErpcoachQuizQuery,
  useGetErpcoachFlashcardsQuery,
  type ErpcoachQuizQuestion,
  type ErpcoachFlashcard,
} from '../../store/api/erpcoachCoachApi'
const DEFAULT_VIDEO_ID = import.meta.env.VITE_ERPCOACH_DEFAULT_VIDEO_ID || 'cwtZSdYE7bo'

/** Tóm tắt dummy — phiên tòa giả định mua bán ma túy (Quy Nhơn, 10/2018), bản dịch từ nội dung mẫu. */
const MOCK_DRUG_TRIAL_LEVELS: { label: string; text: string }[] = [
  {
    label: 'Cơ bản',
    text:
      'Phiên tòa giả định được tổ chức ngày 30/10/2018 tại Đại học Quy Nhơn nhằm minh họa cách tòa án xử lý các vụ buôn bán ma túy. Phiên có ba bị cáo bị cáo buộc mua bán trái phép methamphetamine — chất ma túy nguy hiểm mà Nhà nước nghiêm cấm. Vụ án cho thấy cách hệ thống tư pháp truy cứu tội phạm ma túy và giáo dục công chúng về hậu quả nghiêm trọng của các hành vi liên quan đến ma túy.',
  },
  {
    label: 'Trung cấp',
    text:
      'Viện kiểm sát trình bày chứng cứ cho thấy Võ Văn Lâm đã mua khoảng 6.511 gam methamphetamine từ hai nguồn trong khoảng tháng 2–3/2018, chia nhỏ và bán 39 gói (tổng cộng 4,9 gam) cho năm người mua, thu lợi bất chính 19,5 triệu đồng. Hoàng Thị Thanh Nghĩa (chưa thành niên) giúp Lâm bán trực tiếp methamphetamine bốn lần cho ba người dùng. Trần Thị Trung (sinh viên) mua của Lâm mười ba lần để sử dụng và bị bắt ngày 01/04/2018 khi mang theo 1,5 gam. Tòa áp dụng tình tiết tăng nặng đối với Lâm do bán cho nhiều người lặp lại, đồng thời công nhận tình tiết giảm nhẹ như các bị cáo nhận tội và Lâm tự nguyện nộp lại 12.987.000 đồng tiền lợi bất chính.',
  },
  {
    label: 'Nâng cao',
    text:
      'Bản án phản ánh nguyên tắc buôn bán ma túy gây hại lớn cho xã hội, nhất là khi nhắm vào thanh thiếu niên và sinh viên, nhưng vẫn cân nhắc các tình tiết giảm nhẹ: lời khai thành khẩn trong điều tra và xét xử, việc Lâm tự nguyện nộp lại tiền thu lợi, và địa vị chưa thành niên của Nghĩa theo pháp luật tố tụng vị thành niên. VKS và luật sư tranh luận việc áp dụng nhiều tình tiết tăng nặng cho một chuỗi hành vi của Lâm có phù hợp pháp luật hay không; bên bào chữa cho rằng một hành vi không nên kích hoạt hai quy định tăng nặng tách biệt. Tòa cuối cùng tuyên Lâm 16 tháng tù, Nghĩa 12 tháng, Trung 12–18 tháng — thể hiện cách tòa cân đo vai trò, mức độ lỗi và khả năng cải tạo, đồng thời vẫn răn đe đối với mạng lưới buôn bán ma túy.',
  },
]

const MOCK_DRUG_TRIAL_BULLETS = [
  'Trước đây phiên tòa giả định được coi là hình thức giáo dục pháp luật hiệu quả cho mọi tầng lớp, nhưng số lần tổ chức đã giảm trong những năm gần và không còn bắt buộc.',
  'Phiên tòa giả định dựa trên vụ án thật trở thành phương án thay thế phổ biến do nhiều VKSND cấp tỉnh lựa chọn; VKSND Bình Định đã tổ chức hơn 30 phiên trong các năm gần đây.',
  'Phiên tòa giả định trình diễn liên quan vụ buôn bán ma túy do VKSND Bình Định phối hợp Đại học Quy Nhơn tổ chức tháng 10/2018.',
  'Từ tháng 2 đến tháng 3/2018, bị cáo Võ Văn Lâm mua khoảng 6.511 gam methamphetamine từ hai nguồn và chia nhỏ để bán lại.',
  'Võ Văn Lâm bán methamphetamine 39 lần cho 5 người mua khác nhau, thu 19,5 triệu đồng với lợi nhuận bất chính khoảng 12,987 triệu đồng.',
  'Đồng phạm Hoàng Thị Thanh Nghĩa (lúc đó chưa thành niên) trực tiếp giúp Võ Văn Lâm bán ma túy 4 lần cho 3 người dùng, được hứa hưởng ma túy miễn phí.',
  'Bị cáo Trần Thị Trung mua methamphetamine của Võ Văn Lâm 13 lần từ tháng 2 đến ngày 01/04/2018 để sử dụng cá nhân, trước khi bị bắt khi mang theo 1,5 gam.',
  'Kiểm sát viên đề nghị mức án 9–15 năm đối với Võ Văn Lâm, 5–7 năm đối với Hoàng Thị Thanh Nghĩa, và 12–18 tháng đối với Trần Thị Trung theo các điều luật về buôn bán và tàng trữ ma túy.',
  'Tòa tuyên Võ Văn Lâm 6 năm tù, Hoàng Thị Thanh Nghĩa 5 năm tù, Trần Thị Trung 12 tháng tù; mỗi bị cáo phải nộp 1,2 triệu đồng án phí.',
  'Tòa ra lệnh tịch thu 1 gói methamphetamine, 12,987 triệu đồng tiền lợi bất chính và ba điện thoại di động của các bị cáo.',
]

type ChatTurn = { role: 'user' | 'assistant'; text: string }

/** Kịch bản hội thoại mẫu — hiển thị sẵn nhiều lượt (người xem ↔ trợ lý) minh họa diễn biến phiên tòa ma túy. */
const CHAT_OPENING_DIALOGUE: ChatTurn[] = [
  {
    role: 'assistant',
    text:
      'Chủ tọa tuyên bố khai mạc phiên xét xử sơ thẩm. Vụ án về tội mua bán trái phép chất ma túy; các bị cáo Võ Văn Lâm, Hoàng Thị Thanh Nghĩa và Trần Thị Trung đã được gọi tên và xác nhận có mặt. Đại diện Viện kiểm sát được mời trình bày luận tội.',
  },
  {
    role: 'user',
    text: 'Viện kiểm sát luận tội thế nào với Võ Văn Lâm?',
  },
  {
    role: 'assistant',
    text:
      'Theo luận tội, Lâm bị cáo buộc đã mua khoảng 6.511 gam methamphetamine từ hai nguồn trong tháng 2–3/2018, chia nhỏ và bán lẻ nhiều lần, thu lợi bất chính lớn. Hành vi được xác định thuộc nhóm mua bán trái phép chất ma túy.',
  },
  {
    role: 'user',
    text: 'Hoàng Thị Thanh Nghĩa bị quy kết vai trò gì?',
  },
  {
    role: 'assistant',
    text:
      'Nghĩa lúc đó chưa thành niên, được xác định đã giúp Lâm bán trực tiếp bốn lần cho ba người mua; có tình tiết được hứa cho sử dụng ma túy miễn phí. Tòa sẽ áp dụng quy định riêng về người chưa thành niên phạm tội khi xét vai trò và hình phạt.',
  },
  {
    role: 'user',
    text: 'Còn Trần Thị Trung — hành vi được xác định ra sao?',
  },
  {
    role: 'assistant',
    text:
      'Trung là sinh viên, mua của Lâm mười ba lần để sử dụng cá nhân và bị bắt khi đang mang 1,5 gam. Cần phân biệt rõ vai trò người phân phối với người mua, tàng trữ để dùng.',
  },
  {
    role: 'user',
    text: 'Phần tranh luận giữa Viện kiểm sát và luật sư có điểm gì đáng chú ý?',
  },
  {
    role: 'assistant',
    text:
      'Viện kiểm sát nhấn mạnh tình tiết tăng nặng do bán cho nhiều người và lặp lại. Luật sư bào chữa nêu giảm nhẹ: các bị cáo nhận tội thành khẩn và Lâm đã nộp lại 12.987.000 đồng tiền lợi bất chính. Hai bên tranh luận việc có áp dụng chồng chất nhiều tình tiết tăng nặng cho một chuỗi hành vi hay không.',
  },
  {
    role: 'user',
    text: 'Hội đồng xét xử tuyên án và xử lý tang vật thế nào?',
  },
  {
    role: 'assistant',
    text:
      'Tòa tuyên Võ Văn Lâm 6 năm tù, Hoàng Thị Thanh Nghĩa 5 năm tù, Trần Thị Trung 12 tháng tù; mỗi người nộp 1,2 triệu đồng án phí. Ngoài hình phạt tù, tòa ra quyết định tịch thu tang vật ma túy, số tiền lợi bất chính và điện thoại liên quan. Chủ tọa nhấn mạnh tác hại của ma túy đối với cá nhân và cộng đồng.',
  },
  {
    role: 'assistant',
    text:
      'Chủ tọa tuyên bố kết thúc phần xét xử sơ thẩm và hướng dẫn các bên về quyền kháng cáo trong thời hạn luật định.',
  },
]

/** Khi người dùng gửi thêm câu hỏi sau kịch bản mở đầu — luân phiên, không trùng toàn bộ đoạn trên. */
const CHAT_FOLLOWUP_REPLIES: string[] = [
  'Theo diễn biến phiên, kiểm sát viên đề nghị mức án 9–15 năm đối với Lâm và 5–7 năm đối với Nghĩa — cao hơn mức tòa tuyên; điểm này thường được phân tích khi so sánh luận tội và bản án.',
  'Hội đồng xét xử đã cân nhắc mức độ gây hại cho xã hội, nhất là khi hành vi liên quan thanh thiếu niên và môi trường học đường, song vẫn phải phù hợp các Điều luật và tình tiết giảm nhẹ đã ghi nhận.',
  'Tổng số tiền lợi bất chính khoảng 12,987 triệu đồng đã nêu trong luận tội được xử lý theo quyết định tịch thu của tòa.',
  'Các bị cáo đều có lời khai nhận tội trong giai đoạn điều tra — tòa ghi nhận khi cân nhắc tình tiết giảm nhẹ tại phiên xét xử.',
]

type LearningTab = 'summary' | 'quiz' | 'flashcards'
type SummaryMode = 'level' | 'bullets' | 'timeline'

function mapQuizFromApi(raw: { questions?: unknown[] } | undefined): ErpcoachQuizQuestion[] {
  const qs = raw?.questions
  if (!Array.isArray(qs)) return []
  return qs.map((item: unknown) => {
    const q = item as {
      question?: string
      answers?: string[]
      correct_answer?: string
      explanation?: string
    }
    const answers = Array.isArray(q.answers) ? q.answers : []
    const idx = answers.indexOf(q.correct_answer ?? '')
    return {
      question: String(q.question ?? ''),
      answers,
      correctAnswer: idx >= 0 ? String(idx + 1) : '1',
      explanation: q.explanation || '',
    }
  })
}

function getTranscriptSegments(data: Record<string, unknown> | undefined): { start: number; end: number; text: string }[] {
  if (!data) return []
  const t = data.transcript as Record<string, unknown> | undefined
  const segs = (t?.segments ?? data.segments) as unknown
  if (!Array.isArray(segs)) return []
  return segs.map((s: unknown) => {
    const o = s as { start?: number; end?: number; text?: string }
    const start = o.start ?? 0
    return { start, end: o.end ?? start + 60, text: String(o.text ?? '') }
  })
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const sec = Math.floor(seconds % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

/** Một segment đang phát duy nhất: [start, next.start) tránh trùng biên; segment cuối [start, end]. */
function getActiveTimelineIndex(items: { start: number; end: number }[], t: number): number {
  if (!items.length) return -1
  for (let i = 0; i < items.length; i++) {
    const cur = items[i]
    if (i < items.length - 1) {
      const next = items[i + 1]
      if (t >= cur.start && t < next.start) return i
    } else {
      if (t >= cur.start && t <= cur.end) return i
    }
  }
  return -1
}

export default function CourtSecretaryPage() {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const videoId = searchParams.get('v') || DEFAULT_VIDEO_ID
  const evidenceAnchorRef = useRef<HTMLDivElement | null>(null)

  const [tab, setTab] = useState<LearningTab>('summary')
  const [sumMode, setSumMode] = useState<SummaryMode>('level')

  const youtubeMountRef = useRef<HTMLDivElement | null>(null)
  const { seekTo, playbackTime } = useYoutubeIframePlayer(videoId, youtubeMountRef)

  const [quizIndex, setQuizIndex] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [showQuizResult, setShowQuizResult] = useState(false)

  const [flashIndex, setFlashIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)

  const [chatInput, setChatInput] = useState('')
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; text: string }[]>([])
  const [chatSending, setChatSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatScriptTurnRef = useRef(0)

  const { data: transcriptData, isLoading: txLoading, error: txError, refetch: refetchTx } = useGetErpcoachTranscriptQuery(videoId)
  const { data: quizRaw, isLoading: quizLoading, isError: quizErr } = useGetErpcoachQuizQuery(videoId, { skip: tab !== 'quiz' })
  const { data: fcRaw, isLoading: fcLoading, isError: fcErr } = useGetErpcoachFlashcardsQuery(videoId, { skip: tab !== 'flashcards' })
  const questions = useMemo(() => mapQuizFromApi(quizRaw), [quizRaw])
  const flashcards: ErpcoachFlashcard[] = fcRaw?.flashcards?.length ? fcRaw.flashcards : []
  const currentQ = questions[quizIndex]
  const currentCard = flashcards[flashIndex]

  const levels = MOCK_DRUG_TRIAL_LEVELS
  const bullets = MOCK_DRUG_TRIAL_BULLETS
  const timelineItems = useMemo(() => getTranscriptSegments(transcriptData), [transcriptData])
  const activeTimelineIndex = useMemo(
    () => getActiveTimelineIndex(timelineItems, playbackTime),
    [timelineItems, playbackTime]
  )

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    chatScriptTurnRef.current = 0
    setMessages([...CHAT_OPENING_DIALOGUE])
  }, [videoId])

  useEffect(() => {
    if (searchParams.get('section') !== 'evidence') return
    const el = evidenceAnchorRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [searchParams])

  const handleQuizSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selected === null || !currentQ) return
    setSubmitted(true)
  }

  const handleQuizNext = () => {
    if (!currentQ) return
    if (quizIndex >= questions.length - 1) {
      setShowQuizResult(true)
    } else {
      setQuizIndex((i) => i + 1)
      setSelected(null)
      setSubmitted(false)
    }
  }

  const handleSendChat = () => {
    const t = chatInput.trim()
    if (!t || chatSending) return
    setMessages((m) => [...m, { role: 'user', text: t }])
    setChatInput('')
    setChatSending(true)
    const turn = chatScriptTurnRef.current % CHAT_FOLLOWUP_REPLIES.length
    chatScriptTurnRef.current += 1
    const reply = CHAT_FOLLOWUP_REPLIES[turn]
    window.setTimeout(() => {
      setMessages((m) => [...m, { role: 'assistant', text: reply }])
      setChatSending(false)
    }, 500)
  }

  const summaryBusy = false
  const isAnswerCorrect = submitted && selected === currentQ?.correctAnswer

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100vh - 112px)',
        maxHeight: 'calc(100vh - 112px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.100',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 0, bgcolor: 'background.paper', px: { xs: 2, sm: 3 }, py: 2, flexShrink: 0 }}>
        <Typography variant="h6" component="h1" fontWeight={700} color="primary.dark">
          Thư ký tòa án
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Theo dõi nội dung (video), tóm tắt, Quiz, Flashcards và chat — tích hợp API cùng n2nai_erpcoach
        </Typography>
      </Paper>

      {txError != null ? (
        <Alert severity="warning" sx={{ borderRadius: 0 }} onClose={() => refetchTx()}>
          Không tải được transcript từ ERP Coach. Vẫn xem được video; tóm tắt có thể dùng dữ liệu mẫu.{' '}
          <Button size="small" onClick={() => refetchTx()}>
            Thử lại
          </Button>
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1.5, sm: 2 },
          gap: 2,
          minHeight: 0,
        }}
      >
        {/* Chiều cao video ≈ 1/3 viewport */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 640, md: 720 },
            mx: 'auto',
            mb: { xs: 1.5, sm: 2 },
            height: '33.333vh',
            flexShrink: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              height: '100%',
              boxShadow: `0 4px 18px ${alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: 'grey.900' }}>
              <Box
                ref={youtubeMountRef}
                title="Video — nhấn mốc Timeline để nhảy tới đoạn tương ứng"
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden' }}
              />
            </Box>
          </Paper>
        </Box>

        {txLoading && <LinearProgress sx={{ flexShrink: 0, mb: 1 }} />}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gridTemplateRows: { xs: 'auto auto', md: '1fr' },
            gap: 2,
            alignItems: 'stretch',
            flex: '0 0 auto',
            minHeight: { xs: 'auto', md: 'min(52vh, 540px)' },
            height: { xs: 'auto', md: 'min(52vh, 540px)' },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'primary.contrastText',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <MenuBook fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Tóm tắt
                </Typography>
              </Stack>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={tab}
                onChange={(_, v) => v && setTab(v)}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.25,
                    py: 0.5,
                    fontSize: '0.75rem',
                    color: 'primary.contrastText',
                    borderColor: alpha('#fff', 0.4),
                    '&.Mui-selected': {
                      bgcolor: alpha('#fff', 0.95),
                      color: 'primary.dark',
                      '&:hover': { bgcolor: alpha('#fff', 0.9) },
                    },
                  },
                }}
              >
                <ToggleButton value="summary">Tóm tắt</ToggleButton>
                <ToggleButton value="quiz">
                  <QuizIcon sx={{ fontSize: 18, mr: 0.5 }} /> Quiz
                </ToggleButton>
                <ToggleButton value="flashcards">
                  <FlashcardIcon sx={{ fontSize: 18, mr: 0.5 }} /> Thẻ
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {tab === 'summary' && (
                <>
                  <Tabs
                    value={sumMode}
                    onChange={(_, v) => setSumMode(v)}
                    sx={{ mb: 2, minHeight: 40 }}
                    variant="scrollable"
                  >
                    <Tab value="level" label="Theo cấp độ" sx={{ textTransform: 'none' }} />
                    <Tab value="bullets" label="Gạch đầu dòng" sx={{ textTransform: 'none' }} />
                    <Tab value="timeline" label="Timeline" sx={{ textTransform: 'none' }} />
                  </Tabs>
                  <PageDataState isLoading={summaryBusy} error={undefined} empty={false} minHeight={200}>
                    {sumMode === 'level' && (
                      <Stack spacing={1.5}>
                        {levels.length === 0 && !summaryBusy && (
                          <Typography variant="body2" color="text.secondary">
                            Chưa có nội dung tóm tắt theo cấp độ.
                          </Typography>
                        )}
                        {levels.map((lv) => (
                          <Paper key={lv.label} variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}>
                            <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                              {lv.label}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65 }}>
                              {lv.text}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                    {sumMode === 'bullets' && (
                      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {bullets.map((line, i) => (
                          <ListItem
                            key={i}
                            alignItems="flex-start"
                            sx={{
                              py: 1.5,
                              px: 1.75,
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              borderLeftWidth: 4,
                              borderLeftColor: 'primary.main',
                              borderRadius: 0,
                              boxShadow: `0 1px 0 ${alpha(theme.palette.common.black, 0.06)}`,
                            }}
                          >
                            <Box
                              aria-hidden
                              sx={{
                                minWidth: 28,
                                height: 28,
                                mr: 1.5,
                                mt: 0.1,
                                borderRadius: 0,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.dark',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {i + 1}
                            </Box>
                            <ListItemText
                              primary={line}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.primary',
                                sx: { lineHeight: 1.75, letterSpacing: 0.01 },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    {sumMode === 'timeline' && (
                      <Stack spacing={0.75}>
                        {timelineItems.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Không có segment transcript để hiển thị timeline.
                          </Typography>
                        )}
                        {timelineItems.map((item, index) => {
                          const isActive = index === activeTimelineIndex
                          return (
                            <ListItemButton
                              key={`${item.start}-${index}`}
                              onClick={() => seekTo(item.start)}
                              sx={{
                                alignItems: 'flex-start',
                                py: 1.25,
                                px: 1.5,
                                borderRadius: 0,
                                border: 1,
                                borderColor: isActive ? 'primary.main' : 'divider',
                                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                                flexDirection: 'column',
                                '&:hover': {
                                  bgcolor: isActive
                                    ? alpha(theme.palette.primary.main, 0.12)
                                    : alpha(theme.palette.grey[500], 0.08),
                                },
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: '100%', mb: 0.5 }}>
                                <PlayArrow sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                                <Typography variant="caption" fontWeight={700} color="primary">
                                  {formatTime(item.start)} – {formatTime(item.end)}
                                </Typography>
                                {isActive && (
                                  <Typography variant="caption" color="secondary" sx={{ ml: 'auto' }}>
                                    Đang phát
                                  </Typography>
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.65, pl: 3.5 }}>
                                {item.text}
                              </Typography>
                            </ListItemButton>
                          )
                        })}
                      </Stack>
                    )}
                  </PageDataState>
                </>
              )}

              {tab === 'quiz' && (
                <PageDataState
                  isLoading={quizLoading}
                  error={quizErr ? 'Không tải được Quiz từ ERP Coach.' : undefined}
                  empty={!quizLoading && !quizErr && questions.length === 0}
                  emptyMessage="Chưa có câu hỏi. Thử video ID khác hoặc kiểm tra API."
                  minHeight={240}
                >
                  {!showQuizResult && currentQ && (
                    <Box component="form" onSubmit={handleQuizSubmit}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                        Câu {quizIndex + 1}/{questions.length}: {currentQ.question}
                      </Typography>
                      <Stack spacing={1}>
                        {currentQ.answers.map((ans, aIdx) => {
                          const val = String(aIdx + 1)
                          const isCorrect = submitted && val === currentQ.correctAnswer
                          const isWrong = submitted && val === selected && val !== currentQ.correctAnswer
                          return (
                            <Button
                              key={aIdx}
                              type="button"
                              fullWidth
                              variant={selected === val ? 'contained' : 'outlined'}
                              color={isCorrect ? 'success' : isWrong ? 'error' : 'primary'}
                              disabled={submitted}
                              onClick={() => !submitted && setSelected(val)}
                              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                            >
                              {ans}
                            </Button>
                          )
                        })}
                      </Stack>
                      {submitted && (
                        <Alert severity={isAnswerCorrect ? 'success' : 'warning'} sx={{ mt: 2 }}>
                          {currentQ.explanation || (isAnswerCorrect ? 'Chính xác.' : 'Xem lại đáp án đúng trong danh sách.')}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button size="small" disabled={quizIndex === 0} onClick={() => { setQuizIndex((i) => i - 1); setSelected(null); setSubmitted(false) }}>
                          Trước
                        </Button>
                        {!submitted ? (
                          <Button type="submit" size="small" variant="contained" disabled={selected === null}>
                            Trả lời
                          </Button>
                        ) : (
                          <Button size="small" variant="contained" onClick={handleQuizNext}>
                            {quizIndex >= questions.length - 1 ? 'Xem kết quả' : 'Câu sau'}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  )}
                  {showQuizResult && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Hoàn thành Quiz
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Bạn đã làm qua {questions.length} câu hỏi.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowQuizResult(false)
                          setQuizIndex(0)
                          setSelected(null)
                          setSubmitted(false)
                        }}
                      >
                        Làm lại
                      </Button>
                    </Box>
                  )}
                </PageDataState>
              )}

              {tab === 'flashcards' && (
                <PageDataState
                  isLoading={fcLoading}
                  error={fcErr ? 'Không tải được Flashcards từ ERP Coach.' : undefined}
                  empty={!fcLoading && !fcErr && flashcards.length === 0}
                  emptyMessage="Chưa có thẻ ghi nhớ."
                  minHeight={240}
                >
                  {currentCard && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Card
                        variant="outlined"
                        onClick={() => setFlipped(!flipped)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <CardContent>
                          <Typography variant="body2" fontWeight={600}>
                            {flipped ? currentCard.answer : currentCard.question}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {flipped ? 'Nhấn để xem câu hỏi' : 'Nhấn để xem đáp án'}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          disabled={flashIndex === 0}
                          onClick={() => {
                            setFlashIndex((i) => i - 1)
                            setFlipped(false)
                          }}
                        >
                          Trước
                        </Button>
                        <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                          {flashIndex + 1} / {flashcards.length}
                        </Typography>
                        <Button
                          size="small"
                          disabled={flashIndex >= flashcards.length - 1}
                          onClick={() => {
                            setFlashIndex((i) => i + 1)
                            setFlipped(false)
                          }}
                        >
                          Sau
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </PageDataState>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Đặt câu hỏi
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                Gõ câu hỏi để làm rõ luận tội, vai trò từng bị cáo, phần tranh luận hoặc quyết định của Hội đồng xét xử — phù hợp khi vừa xem video vừa cần tra cứu nhanh.
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {messages.map((msg, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                  {msg.role === 'assistant' && (
                    <SmartToy sx={{ fontSize: 20, color: 'primary.main', mr: 1, flexShrink: 0, mt: 0.25 }} />
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '88%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                      color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      border: msg.role === 'assistant' ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      {msg.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
              {chatSending && <LinearProgress sx={{ mt: 1 }} />}
              <div ref={messagesEndRef} />
            </Box>
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, bgcolor: 'background.paper' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Nhập câu hỏi..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  // Tránh gửi nhầm khi đang gõ tiếng Việt/IME (Enter để chọn từ) hoặc khi muốn xuống dòng.
                  const composing = Boolean((e as unknown as { isComposing?: boolean }).isComposing || (e.nativeEvent as unknown as { isComposing?: boolean }).isComposing)
                  if (e.key === 'Enter' && !e.shiftKey && !composing) {
                    e.preventDefault()
                    handleSendChat()
                  }
                }}
                disabled={chatSending}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <IconButton color="primary" onClick={handleSendChat} disabled={!chatInput.trim() || chatSending} aria-label="Gửi">
                <Send />
              </IconButton>
            </Box>
          </Paper>
        </Box>

        <Box
          ref={evidenceAnchorRef}
          id="court-secretary-evidence"
          sx={{ flexShrink: 0, scrollMarginTop: 16 }}
        >
          <CourtSecretaryEvidencePanel />
        </Box>
      </Box>
    </Box>
  )
}
